import { useCallback, useEffect, useRef, useState } from "react";

const DEFAULT_INTERVAL_MS = 4000;
const DEFAULT_MAX_ATTEMPTS = 30;

/**
 * Poll a lazy RTK Query trigger until status === "completed".
 *
 * @param {Function} fetchFn - async () => data | throws; should return { status }
 * @param {object} options
 * @param {boolean} options.enabled - start polling when true
 * @param {number} options.intervalMs
 * @param {number} options.maxAttempts
 * @param {Function} options.onCompleted
 * @param {Function} options.onTimeout
 * @param {Function} options.onError
 */
export function useAiPoll(
  fetchFn,
  {
    enabled = false,
    intervalMs = DEFAULT_INTERVAL_MS,
    maxAttempts = DEFAULT_MAX_ATTEMPTS,
    onCompleted,
    onTimeout,
    onError,
  } = {},
) {
  const [isPolling, setIsPolling] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [timedOut, setTimedOut] = useState(false);

  const fetchRef = useRef(fetchFn);
  const onCompletedRef = useRef(onCompleted);
  const onTimeoutRef = useRef(onTimeout);
  const onErrorRef = useRef(onError);
  const timerRef = useRef(null);
  const attemptsRef = useRef(0);
  const activeRef = useRef(false);

  fetchRef.current = fetchFn;
  onCompletedRef.current = onCompleted;
  onTimeoutRef.current = onTimeout;
  onErrorRef.current = onError;

  const stop = useCallback(() => {
    activeRef.current = false;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setIsPolling(false);
  }, []);

  const tick = useCallback(async () => {
    if (!activeRef.current) return;

    attemptsRef.current += 1;
    setAttempts(attemptsRef.current);

    if (attemptsRef.current > maxAttempts) {
      stop();
      setTimedOut(true);
      onTimeoutRef.current?.();
      return;
    }

    try {
      const data = await fetchRef.current();
      if (!activeRef.current) return;

      if (data?.status === "completed") {
        stop();
        onCompletedRef.current?.(data);
        return;
      }

      if (data?.status === "failed" || data?.status === "error") {
        stop();
        onErrorRef.current?.(data);
        return;
      }

      timerRef.current = setTimeout(tick, intervalMs);
    } catch (err) {
      if (!activeRef.current) return;
      // 404 not_generated during poll should keep waiting after POST pending
      if (err?.status === 404) {
        timerRef.current = setTimeout(tick, intervalMs);
        return;
      }
      stop();
      onErrorRef.current?.(err);
    }
  }, [intervalMs, maxAttempts, stop]);

  const start = useCallback(() => {
    stop();
    attemptsRef.current = 0;
    setAttempts(0);
    setTimedOut(false);
    activeRef.current = true;
    setIsPolling(true);
    tick();
  }, [stop, tick]);

  useEffect(() => {
    if (enabled) start();
    else stop();
    return stop;
  }, [enabled, start, stop]);

  return { isPolling, attempts, timedOut, start, stop };
}

export default useAiPoll;
